/**
 * `/v1/stages` — the effective stage list of one årgang, and the festival's standard set.
 *
 * ---------------------------------------------------------------------------------------------
 * ONE ENDPOINT, TWO SCOPES, AND THE CALLER PICKS BY SENDING `editionId` OR NOT.
 *
 * `contracts/stages.ts` spends a paragraph on why that is one route rather than two: "they answer
 * the same question at two scopes and a second route would duplicate the resolution rule in a
 * second place". So this module is one query with a nullable årgang rather than two functions, and
 * `qk.stageList` carries the scope as a discriminator for the same reason — the standard set and
 * the year's effective list are two different answers about the same festival.
 *
 * WHY IT LIVES IN `api/queries/` RATHER THAN IN A FEATURE. Two features read it: the line-up's
 * stage picker and stage filter (this delivery) and the stages panel that administers the rows
 * (its own). `contacts`, `editions` and `festivals` are here for exactly that reason, and the
 * house rule is one implementation per cross-cutting concern — a second `stagesQueryOptions`
 * inside a feature would be a second cache key for the same rows and the first one to go stale.
 *
 * REFERENCE TIER, NOT LIST TIER. A stage is what other lists FILTER BY — `cache-tiers.ts` names
 * it in as many words beside departments and tags. A line-up is edited all day; the four stages it
 * is played on are set once a year and then read on every request.
 * ---------------------------------------------------------------------------------------------
 */
import { MAX_PAGE_SIZE, stageListResponseSchema, type StageListResponse } from '@tvitvi/shared';
import { queryOptions } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const PATH = '/stages';

/**
 * Every stage the årgang can see, in the festival's own order.
 *
 * `editionId` NULL asks for the festival's standard set; an id asks for that year's effective
 * list — the standard set with the year's overrides applied. A picker on a line-up always sends
 * the year: `contracts/stages.ts` is explicit that "a performance may only use a stage its edition
 * can see", so a picker filled from the festival's set alone would offer this year's tent to
 * nobody and last year's to everybody.
 *
 * ONE PAGE. `MAX_PAGE_SIZE` is 100 and a festival with more than a hundred stages is a city, not
 * an event. Unlike the line-up this does not surface a truncation line of its own — a picker is
 * not a list somebody reads to the end — but the request is still explicit about asking for one
 * page rather than pretending the endpoint is unpaginated.
 */
export function stagesQueryOptions(orgId: string, festivalId: string, editionId: string | null) {
  return queryOptions({
    queryKey: qk.stageList(orgId, festivalId, editionId),
    queryFn: ({ signal }): Promise<StageListResponse> =>
      apiRequest({
        path: PATH,
        // Spread rather than assigned: `buildUrl` drops an `undefined` value but the key is still
        // a key, and "this request asked about the festival and not about a year" is a property a
        // test has to be able to assert on the request itself.
        query: {
          festivalId,
          sort: 'order',
          direction: 'asc',
          limit: MAX_PAGE_SIZE,
          ...(editionId !== null ? { editionId } : {}),
        },
        schema: stageListResponseSchema,
        signal,
      }),
    ...CACHE.reference,
  });
}
